import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { 
  BookOpen, FileText, Brain, Upload, Plus, Coins, 
  BarChart3, ChevronRight, GraduationCap, Users, Sparkles, UserPlus, School
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import EnableNotificationsBanner from "../components/EnableNotificationsBanner";
import OnboardingStepper from "../components/OnboardingStepper";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBooks, setRecentBooks] = useState([]);
  const [myClassrooms, setMyClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joiningClassroom, setJoiningClassroom] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/stats`, { headers: getAuthHeaders(token) }),
        axios.get(`${API}/books`, { headers: getAuthHeaders(token) }),
        axios.get(`${API}/classrooms`, { headers: getAuthHeaders(token) })
      ];
      
      const [statsRes, booksRes, classroomsRes] = await Promise.all(requests);
      setStats(statsRes.data);
      // Sort books by created_at descending (most recent first) and take top 3
      const sortedBooks = [...booksRes.data].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setRecentBooks(sortedBooks.slice(0, 3));
      setMyClassrooms(classroomsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClassroom = async () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a classroom code");
      return;
    }
    
    setJoiningClassroom(true);
    try {
      const response = await axios.post(`${API}/classrooms/join`, 
        { join_code: joinCode.trim().toUpperCase() },
        { headers: getAuthHeaders(token) }
      );
      toast.success(`Joined classroom: ${response.data.classroom_name}`);
      setShowJoinDialog(false);
      setJoinCode("");
      // Navigate to the classroom or classrooms page
      navigate("/classrooms");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Failed to join classroom. Check the code and try again.");
      }
    } finally {
      setJoiningClassroom(false);
    }
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case "teacher":
        return <GraduationCap className="w-4 h-4" />;
      case "parent":
        return <Users className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      {/* Header */}
      <DashboardHeader />

      {/* Main Content */}
      <main className="container-main py-8">
        {/* Push notifications opt-in banner */}
        <EnableNotificationsBanner />

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600">
            {t('dashboard.overview')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 lg:grid-cols-5 gap-6 mb-8">
          <Card 
            className="card-base card-interactive group cursor-pointer"
            onClick={() => navigate("/books/upload")}
            data-testid="upload-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Upload className="w-6 h-6 text-[#4F46E5]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{t('dashboard.uploadBook')}</h3>
              <p className="text-sm text-slate-500">PDF, URL, {t('common.or')} text</p>
            </CardContent>
          </Card>

          <Card 
            className="card-base card-interactive group cursor-pointer"
            onClick={() => navigate("/tests")}
            data-testid="tests-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-[#14B8A6]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{t('tests.title')}</h3>
              <p className="text-sm text-slate-500">{stats?.tests || 0} tests</p>
            </CardContent>
          </Card>

          <Card 
            className="card-base card-interactive group cursor-pointer"
            onClick={() => navigate("/quizzes")}
            data-testid="quizzes-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Brain className="w-6 h-6 text-[#F97316]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{t('quizzes.title')}</h3>
              <p className="text-sm text-slate-500">{stats?.quizzes || 0} quizzes</p>
            </CardContent>
          </Card>

          <Card 
            className="card-base card-interactive group cursor-pointer"
            onClick={() => navigate("/lecture-notes")}
            data-testid="notes-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{t('notes.title')}</h3>
              <p className="text-sm text-slate-500">{stats?.lecture_notes || 0} notes</p>
            </CardContent>
          </Card>

          {/* Join Classroom Card - Visible to everyone */}
          <Card 
            className="card-base card-interactive group cursor-pointer border-2 border-dashed border-green-200 hover:border-green-400"
            onClick={() => setShowJoinDialog(true)}
            data-testid="join-classroom-card"
          >
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <UserPlus className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{t('classrooms.joinClassroom')}</h3>
              <p className="text-sm text-slate-500">{t('classrooms.enterCode')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#4F46E5]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.books || 0}</p>
                <p className="text-sm text-slate-500">{t('nav.books')}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#14B8A6]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.tests || 0}</p>
                <p className="text-sm text-slate-500">Tests</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-[#F97316]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.quiz_attempts || 0}</p>
                <p className="text-sm text-slate-500">Quizzes</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.average_score || 0}%</p>
                <p className="text-sm text-slate-500">{t('quizzes.score')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Books */}
        <OnboardingStepper />
        <Card className="card-base">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('dashboard.recentBooks')}</CardTitle>
            <Link to="/books">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-books-btn">
                {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentBooks.length > 0 ? (
              <div className="space-y-3">
                {recentBooks.map((book) => (
                  <div 
                    key={book.book_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/books/${book.book_id}/chapters`)}
                    data-testid={`book-item-${book.book_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-[#4F46E5]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{book.title}</p>
                        <p className="text-sm text-slate-500">
                          {book.chapters?.length || 0} {t('books.chapters')} • {book.status}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                  <BookOpen className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-medium text-slate-900 mb-2">{t('dashboard.noBooks')}</h3>
                <p className="text-slate-500 mb-4">{t('dashboard.uploadFirst')}</p>
                <Button 
                  onClick={() => navigate("/books/upload")}
                  className="btn-primary"
                  data-testid="upload-first-book-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.uploadBook')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Classrooms Section */}
        {myClassrooms.length > 0 && (
          <Card className="card-base mt-6">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
                <School className="w-5 h-5 text-[#4F46E5]" />
                {t('classrooms.title')}
              </CardTitle>
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => setShowJoinDialog(true)}>
                <Plus className="w-4 h-4" /> {t('classrooms.joinClassroom')}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myClassrooms.map((classroom) => (
                  <div
                    key={classroom.classroom_id}
                    className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/classrooms/${classroom.classroom_id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-[#4F46E5] rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex items-center gap-1 text-xs text-indigo-600 bg-white px-2 py-1 rounded-full">
                        <FileText className="w-3 h-3" />
                        {classroom.shared_tests?.length || 0} tests
                      </div>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1">{classroom.name}</h4>
                    <p className="text-sm text-slate-500 mb-3">
                      {classroom.shared_notes?.length || 0} notes
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {classroom.students?.length || 0} {t('classrooms.students')}
                      </span>
                      <Button variant="ghost" size="sm" className="h-7 text-[#4F46E5] hover:bg-indigo-100 gap-1">
                        {t('common.view')} <ChevronRight className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credits Banner */}
        {user?.credits < 20 && (
          <Card className="mt-6 bg-gradient-to-r from-[#4F46E5] to-[#14B8A6] text-white">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{t('errors.insufficientCredits')}!</h3>
                  <p className="text-white/80">{t('pricing.creditDesc')}</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/pricing")}
                className="bg-white text-[#4F46E5] hover:bg-slate-50"
                data-testid="buy-credits-banner-btn"
              >
                {t('pricing.buyCredits')}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Join Classroom Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              {t('classrooms.joinClassroom')}
            </DialogTitle>
            <DialogDescription>
              {t('classrooms.enterCode')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Input
                placeholder={t('classrooms.inviteCode')}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="text-center text-lg tracking-widest font-mono"
                maxLength={12}
                data-testid="join-classroom-code-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleJoinClassroom}
                disabled={joiningClassroom || !joinCode.trim()}
                className="btn-primary"
                data-testid="join-classroom-submit-btn"
              >
                {joiningClassroom ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    {t('common.loading')}
                  </>
                ) : (
                  t('classrooms.joinClassroom')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
